/**
 * Tab panel bodies for the Flow Optimizer drawer. The tracker's drawer profile
 * picks the Standard editor (Binom's own, or the Voluum-shaped one Voluum and
 * Tres Commas share) and which CUSTOM tabs exist.
 */

import { FC } from 'react';
import { TabsContent } from '@/components/ui/layout/Tabs';
import { matchTracker } from '@trafficpulse/shared';

import StandardConfigTab from './tabs/StandardConfigTab';
import BinomStandardConfigTab from './tabs/BinomStandardConfigTab';
import BinomCampaignTab from './tabs/BinomCampaignTab';
import BinomAdvancedTab from './tabs/BinomAdvancedTab';
import PreviewTab from './tabs/PreviewTab';
import CustomTabPanels from '@/features/flow-optimizer/drawer-shell/CustomTabPanels';
import { PreviewSidePanel } from './components/preview/panel';
import type { FlowOptimizerConfig } from '@trafficpulse/shared';
import type { FlowOptimizerTabKey } from './types/flow-optimizer.types';
import type { FlowOptimizerProvider } from './services/flow-optimizer-api';
import type { DrawerProfile } from '@/features/flow-optimizer/tracker';

export interface FlowOptimizerDrawerTabPanelsProps {
  tab: FlowOptimizerTabKey;
  campaignId: string;
  draft: FlowOptimizerConfig;
  onChange: (next: FlowOptimizerConfig) => void;
  provider: FlowOptimizerProvider;
  profile: DrawerProfile;
  isCustom: boolean;
  splitActive: boolean;
  onOpenPreviewTab: () => void;
  onUnpinPreview: () => void;
}

const FlowOptimizerDrawerTabPanels: FC<FlowOptimizerDrawerTabPanelsProps> = ({
  tab,
  campaignId,
  draft,
  onChange,
  provider,
  profile,
  isCustom,
  splitActive,
  onOpenPreviewTab,
  onUnpinPreview,
}) => {
  const sidePanel = (
    <PreviewSidePanel
      onOpenPreviewTab={onOpenPreviewTab}
      onCollapse={onUnpinPreview}
    />
  );
  const standardTab = (
    <StandardConfigTab
      flowId={draft.flowId}
      killSwitch={draft.killSwitch}
      standardJson={draft.standardJson}
      standardTabActive={tab === 'standard'}
      onChange={(standardJson) => onChange({ ...draft, standardJson })}
    />
  );

  return (
    <>
      {!isCustom ? (
        <TabsContent
          value="standard"
          className="mt-1.5 flex min-h-0 flex-1 flex-col overflow-hidden ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {matchTracker(provider, {
            binom: () => <BinomStandardConfigTab killSwitch={draft.killSwitch} />,
            voluum: () => standardTab,
            'tres-commas': () => standardTab,
          })}
        </TabsContent>
      ) : (
        <TabsContent
          value="preview"
          className="mt-1.5 flex min-h-0 flex-1 flex-col overflow-hidden ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <PreviewTab provider={provider} killSwitch={draft.killSwitch} />
        </TabsContent>
      )}
      {profile.campaignTabs ? (
        <TabsContent
          value="campaign"
          className="mt-1.5 min-h-0 flex-1 overflow-y-auto ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <BinomCampaignTab />
        </TabsContent>
      ) : null}
      {profile.campaignTabs ? (
        <TabsContent
          value="advanced"
          className="mt-1.5 min-h-0 flex-1 overflow-y-auto ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <BinomAdvancedTab />
        </TabsContent>
      ) : null}
      {isCustom ? (
        <CustomTabPanels
          campaignId={campaignId}
          draft={draft}
          onChange={onChange}
          provider={provider}
          profile={profile}
          splitActive={splitActive}
          sidePanel={sidePanel}
        />
      ) : null}
    </>
  );
};

export default FlowOptimizerDrawerTabPanels;
